import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, KeyRound } from "lucide-react";
import { authApi } from "../services/http";
import ThemeToggle from "../components/ThemeToggle";
import "./Login.css";

const schema = z.object({
  email: z.string().trim().min(1, "Indique o e-mail.").email("Indique um e-mail válido."),
});

export default function ForgotPassword() {
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  async function onSubmit(data) {
    setServerError("");
    try {
      await authApi.post("/auth/forgot-password", { email: data.email });
      setDone(true);
    } catch (err) {
      if (err.response?.status === 429) {
        setServerError(
          err.response?.data?.message || "Muitos pedidos. Tente novamente dentro de alguns minutos."
        );
      } else {
        setServerError(
          err.response?.data?.message || "Não foi possível processar o pedido. Tente novamente."
        );
      }
    }
  }

  return (
    <div className="login-bg">
      <div className="login-shell" role="main">
        <div className="login-hero" aria-label="Apresentação do painel da igreja">
          <div className="hero-badge">PAINEL DA IGREJA</div>
          <h2>Recuperar o acesso</h2>
          <p>
            Enviamos uma ligação segura para o seu e-mail para que possa definir uma nova
            palavra-passe e voltar ao painel da liderança.
          </p>
        </div>

        <div className="login-card glass" aria-label="Formulário de recuperação">
          <div className="login-row" style={{ alignItems: "center" }}>
            <div className="login-brand" aria-label="Painel da igreja">
              <span className="dot" />
              Painel da igreja
            </div>
            <ThemeToggle />
          </div>

          <div className="login-lock" aria-hidden="true">
            <KeyRound size={28} strokeWidth={2.25} />
          </div>
          <div className="login-title">Esqueceu-se da palavra-passe?</div>
          <div className="login-subtitle">
            Indique o e-mail da sua conta e receberá instruções para a redefinir.
          </div>

          {serverError && <div className="alert-error">{serverError}</div>}

          {done ? (
            <div className="alert-success" role="status">
              Se existir uma conta associada a esse e-mail, enviámos instruções para recuperar a
              palavra-passe. Verifique também a pasta de spam.
            </div>
          ) : (
            <form className="login-form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <label className="label" htmlFor="email">
                E-mail
              </label>
              <div className="input-group">
                <span className="input-icon" aria-hidden="true">
                  <Mail size={18} strokeWidth={2} />
                </span>
                <input
                  id="email"
                  className={`input ${errors.email || serverError ? "has-error" : ""}`}
                  type="email"
                  autoComplete="email"
                  autoFocus
                  aria-label="E-mail"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "email-error" : undefined}
                  {...register("email")}
                />
              </div>
              {errors.email && (
                <div id="email-error" className="field-hint">
                  {errors.email.message}
                </div>
              )}

              <button
                className="btn-primary btn-gradient"
                type="submit"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? "A enviar..." : "Enviar instruções"}
              </button>
            </form>
          )}

          <div className="login-footer">
            <Link className="link-muted" to="/login">
              <ArrowLeft size={14} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Voltar ao início de sessão
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
